import type { AudioSystem } from './audio';

const SPEAKER_ON = '🔊';
const SPEAKER_OFF = '🔇';

/**
 * A browser will not start audio outside a user gesture, so every game waits for
 * the first pointer anywhere on the page. Returns the undo, because a game that
 * is torn down (HMR, remount) must not leave a listener holding its old audio.
 */
export function unlockAudioOnFirstPointer(audio: AudioSystem): () => void {
  const unlock = (): void => audio.unlock();
  document.addEventListener('pointerdown', unlock, { passive: true, once: true });
  return () => document.removeEventListener('pointerdown', unlock);
}

/**
 * Wire a 🔊 / 🔇 button to the audio system: paints the current state, flips it
 * on click, and unlocks audio on the way (the click is a gesture, so a child who
 * reaches for the speaker first still gets sound).
 *
 * `onChange` is for anything that shadows the mute flag — the maths game silences
 * its speech synthesis there.
 */
export function mountMuteButton(
  button: HTMLElement,
  audio: AudioSystem,
  onChange?: (muted: boolean) => void,
): void {
  const paint = (): void => {
    button.textContent = audio.isMuted ? SPEAKER_OFF : SPEAKER_ON;
  };
  paint();
  onChange?.(audio.isMuted);
  button.addEventListener('click', () => {
    audio.unlock();
    audio.setMuted(!audio.isMuted);
    paint();
    onChange?.(audio.isMuted);
  });
}
